import React, { useState } from 'react';
import { ArrowRight, Lock, ShieldCheck, AlertCircle } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { GlowButton } from '@/components/brand/GlowButton';
import { SectionReveal } from '@/components/brand/SectionReveal';
import { FormField, CurrencyField } from '@/components/forms/FormField';
import { API } from '@/lib/api';
import { toast } from 'sonner';

// --- Sub-components ---------------------------------------------------------

const PaymentHeader = () => (
    <SectionReveal>
        <div className="meta-eyebrow">Secure Payment Portal</div>
        <h1 className="mt-4 font-display font-normal text-[40px] sm:text-[52px] lg:text-[58px] leading-[1.05] tracking-[-0.005em] text-la-text text-balance">
            Make A Payment
        </h1>
        <p className="mt-6 max-w-2xl text-[14.5px] text-la-text2 leading-relaxed">
            Enter the reference number provided by Legal Alliance Consultancy, your details and the agreed amount. You will be redirected to Stripe&rsquo;s secure checkout to complete the payment.
        </p>
    </SectionReveal>
);

const PaymentDisclaimer = () => (
    <SectionReveal delay={0.04}>
        <div className="mt-7 flex items-start gap-3 rounded-la-sm border border-la-gold/30 bg-la-gold/[0.05] px-4 py-3.5">
            <AlertCircle className="h-4 w-4 text-la-gold mt-0.5 shrink-0" />
            <p className="text-[13px] text-la-text2 leading-relaxed">
                Please only make a payment if you have been given a payment reference by Legal Alliance Consultancy Ltd.
            </p>
        </div>
    </SectionReveal>
);

const SecurityBadges = () => (
    <div className="mt-7 grid grid-cols-3 gap-2 max-w-md text-[10px] tracking-[0.20em] uppercase text-la-text3">
        <span className="inline-flex items-center gap-1.5 border border-white/8 rounded-la-sm px-2 py-1.5 justify-center"><Lock className="h-3 w-3" /> TLS 1.3</span>
        <span className="inline-flex items-center gap-1.5 border border-white/8 rounded-la-sm px-2 py-1.5 justify-center"><ShieldCheck className="h-3 w-3" /> PCI</span>
        <span className="inline-flex items-center gap-1.5 border border-white/8 rounded-la-sm px-2 py-1.5 justify-center">Stripe</span>
    </div>
);

const PaymentForm = ({ form, onChange, onSubmit, submitting }) => (
    <SectionReveal delay={0.06} className="mt-8">
        <div className="panel rounded-la p-7 sm:p-10">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="payment-form" noValidate>
                <FormField
                    label="Payment Reference Number"
                    value={form.reference} onChange={onChange('reference')}
                    required requiredMark testId="payment-reference-input"
                    placeholder="Reference provided by the firm"
                    autoComplete="off"
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <FormField label="Client Name" value={form.name} onChange={onChange('name')} required requiredMark testId="payment-name-input" autoComplete="name" />
                    <FormField label="Email Address" type="email" value={form.email} onChange={onChange('email')} required requiredMark testId="payment-email-input" autoComplete="email" />
                </div>
                <CurrencyField
                    label="Payment Amount (GBP)"
                    requiredMark
                    required
                    value={form.amount}
                    onChange={onChange('amount')}
                    testId="payment-amount-input"
                    helper="Amount must be greater than £1.00."
                />
                <div>
                    <label className="meta-eyebrow text-la-text3">Notes</label>
                    <textarea
                        value={form.notes} onChange={onChange('notes')} rows={3}
                        data-testid="payment-notes-input"
                        className="mt-3 w-full rounded-la-sm bg-white/[0.02] border border-white/10 px-4 py-3 text-[14px] text-la-text placeholder:text-la-text3 focus:outline-none focus:border-la-gold/40"
                        placeholder="Optional context for this payment."
                    />
                </div>
                <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
                    <span className="text-[11px] text-la-text3 inline-flex items-center gap-2"><Lock className="h-3 w-3" /> Processed securely by Stripe.</span>
                    <GlowButton type="submit" variant="gold" disabled={submitting} data-testid="payment-submit-button">
                        {submitting ? 'Redirecting…' : <>Pay Now <ArrowRight className="h-4 w-4" /></>}
                    </GlowButton>
                </div>
            </form>
        </div>
    </SectionReveal>
);

// --- Main page --------------------------------------------------------------

const INITIAL_FORM = { name: '', email: '', reference: '', amount: '', notes: '' };

export default function PaymentPage() {
    const [form, setForm] = useState(INITIAL_FORM);
    const [submitting, setSubmitting] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        const amount = parseFloat(form.amount);
        if (!form.reference.trim()) { toast.error('Payment reference is required.'); return; }
        if (!form.name || !form.email) { toast.error('Please complete client name and email.'); return; }
        if (!amount || amount <= 1) { toast.error('Payment amount must be greater than £1.00.'); return; }

        try {
            setSubmitting(true);
            const origin = window.location.origin;
            const res = await fetch(`${API}/create-checkout-session`, {
                method: 'POST',
                // Public endpoint — must NOT include credentials, otherwise the
                // Emergent ingress's `Access-Control-Allow-Origin: *` response
                // is rejected by the browser and the request appears to fail.
                credentials: 'omit',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_name: form.name,
                    client_email: form.email,
                    payment_reference: form.reference.trim(),
                    amount,
                    notes: form.notes,
                    origin_url: origin,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const msg = typeof data?.detail === 'string' ? data.detail : 'Could not initiate the payment.';
                toast.error(msg);
                setSubmitting(false);
                return;
            }
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error('Payment could not be initiated.');
                setSubmitting(false);
            }
        } catch (err) {
            toast.error('Payment could not be initiated. Please try again.');
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-la-canvas text-la-text min-h-screen">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
                    <PaymentHeader />
                    <PaymentDisclaimer />
                    <PaymentForm form={form} onChange={set} onSubmit={submit} submitting={submitting} />
                    <SecurityBadges />
                </div>
            </main>
            <Footer />
        </div>
    );
}
